import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChildProfileFields,
  type ChildFieldsErrors,
  type ChildFieldsValues,
} from '@/features/children/ChildProfileFields';
import {
  DEFAULT_CHILD_FIELDS_ERRORS,
  DEFAULT_CHILD_FIELDS_VALUES,
} from '@/features/children/child-fields-defaults';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { computeAge } from '@/lib/age';
import { ApiError, NetworkError, apiFetch } from '@/lib/api-client';

// Verify-resend constants and behavior mirror BuilderGate.tsx — same
// endpoint, same success+rate-limit cadence — so the unverified-user
// experience here matches the value-action gate downstream.
const RESEND_SUCCESS_DURATION_MS = 3000;
const RESEND_RATE_LIMIT_MS = 60000;
import type { Child, CreateChildRequest, Gender } from '@/lib/auth-types';
import { ASSETS, LEGAL_URLS } from '@/lib/config';
import { splitCSV } from '@/lib/csv';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type FormErrorVariant = 'parentUpdate' | 'childCreate' | 'network' | 'emailNotVerified' | null;

const MAX_CHILD_AGE_YEARS = 13;

export function ChildProfileRoute() {
  const navigate = useNavigate();
  const updateProfile = useAuthStore((s) => s.updateProfile);

  // Parent fields stay onboarding-shell-level (User-entity scope, not
  // Child-entity scope — see PWA/Offen #31 for the future A-04
  // Account-Profile-Page that becomes the canonical surface).
  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastName, setParentLastName] = useState('');
  const [errParentFirstName, setErrParentFirstName] = useState<string | null>(null);
  const [errParentLastName, setErrParentLastName] = useState<string | null>(null);

  // Child fields delegated to the shared ChildProfileFields sub-component.
  const [values, setValues] = useState<ChildFieldsValues>(DEFAULT_CHILD_FIELDS_VALUES);
  const [errors, setErrors] = useState<ChildFieldsErrors>(DEFAULT_CHILD_FIELDS_ERRORS);

  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<FormErrorVariant>(null);

  // Email-resend state — only relevant while formError === 'emailNotVerified'.
  // Same shape as BuilderGate.tsx so the user gets the same loading +
  // 3s success + 60s rate-limit feedback at both surfaces.
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccessUntil, setResendSuccessUntil] = useState<number | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (resendSuccessUntil === null && rateLimitedUntil === null) return;
    const interval = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (resendSuccessUntil !== null && current >= resendSuccessUntil) {
        setResendSuccessUntil(null);
      }
      if (rateLimitedUntil !== null && current >= rateLimitedUntil) {
        setRateLimitedUntil(null);
      }
    }, 250);
    return () => window.clearInterval(interval);
  }, [resendSuccessUntil, rateLimitedUntil]);

  const resendSuccess = resendSuccessUntil !== null && now < resendSuccessUntil;
  const rateLimited = rateLimitedUntil !== null && now < rateLimitedUntil;

  const handleResend = async () => {
    if (resendLoading || rateLimited) return;
    setResendLoading(true);
    try {
      await apiFetch('/api/auth/request-verification', {
        method: 'POST',
        body: {},
      });
      setResendSuccessUntil(Date.now() + RESEND_SUCCESS_DURATION_MS);
      setRateLimitedUntil(Date.now() + RESEND_RATE_LIMIT_MS);
    } catch (err) {
      // 400 ("already verified"): stale state, will resolve when the
      // user re-submits the form. Network / 5xx: silent, user retries.
      console.error('[ChildProfileRoute] resend failed:', err);
    } finally {
      setResendLoading(false);
    }
  };

  const S = STRINGS.onboarding.childProfile;
  const FE = STRINGS.children.fields.errors;

  // ── validators ──
  const validateRequired = (value: string): string | null => (value.trim() ? null : FE.required);

  const validateBirthdate = (value: string): string | null => {
    if (!value) return FE.required;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return FE.invalidDate;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    if (d > now) return FE.futureDate;
    const minD = new Date(now);
    minD.setFullYear(minD.getFullYear() - MAX_CHILD_AGE_YEARS);
    if (d < minD) return FE.tooOld;
    return null;
  };

  const handleChildFieldChange = (patch: Partial<ChildFieldsValues>) => {
    setValues((prev) => ({ ...prev, ...patch }));
    if (!isDirty) setIsDirty(true);
    // Clear inline errors as the user edits.
    if (patch.childName !== undefined && errors.childName) {
      setErrors((e) => ({ ...e, childName: null }));
    }
    if (patch.birthdate !== undefined && errors.birthdate) {
      setErrors((e) => ({ ...e, birthdate: null }));
    }
    if (patch.gender !== undefined && errors.gender) {
      setErrors((e) => ({ ...e, gender: null }));
    }
  };

  const handleChildFieldBlur = (field: 'childName' | 'birthdate' | 'gender') => {
    if (field === 'childName') {
      setErrors((e) => ({ ...e, childName: validateRequired(values.childName) }));
    } else if (field === 'birthdate') {
      setErrors((e) => ({ ...e, birthdate: validateBirthdate(values.birthdate) }));
    } else {
      setErrors((e) => ({ ...e, gender: values.gender ? null : FE.required }));
    }
  };

  const markDirty = () => {
    if (!isDirty) setIsDirty(true);
  };

  const handleBack = () => {
    if (isDirty) {
      const confirmed = window.confirm(S.discardChangesConfirm);
      if (!confirmed) return;
    }
    navigate('/onboarding/welcome');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const errFirst = validateRequired(parentFirstName);
    const errLast = validateRequired(parentLastName);
    const errChildName = validateRequired(values.childName);
    const errBirth = validateBirthdate(values.birthdate);
    const errGen = values.gender ? null : FE.required;

    setErrParentFirstName(errFirst);
    setErrParentLastName(errLast);
    setErrors({ childName: errChildName, birthdate: errBirth, gender: errGen });

    if (errFirst || errLast || errChildName || errBirth || errGen) return;

    setFormError(null);
    setIsSubmitting(true);

    // Step 1: Update parent name
    try {
      await updateProfile({
        firstName: parentFirstName.trim(),
        lastName: parentLastName.trim(),
      });
    } catch (err) {
      setIsSubmitting(false);
      if (err instanceof NetworkError) {
        setFormError('network');
      } else if (err instanceof ApiError && err.httpStatus === 403) {
        setFormError('emailNotVerified');
      } else {
        setFormError('parentUpdate');
      }
      return;
    }

    // Step 2: Create child profile
    try {
      const payload: CreateChildRequest = {
        name: values.childName.trim(),
        age: computeAge(values.birthdate),
        birthdate: values.birthdate,
        gender: values.gender as Gender,
      };
      if (values.nickname.trim()) payload.nickname = values.nickname.trim();
      if (values.kuscheltier.trim()) payload.stuffedAnimalName = values.kuscheltier.trim();
      if (values.sonstiges.trim()) payload.contextNotes = values.sonstiges.trim();
      const interestsList = splitCSV(values.interests);
      if (interestsList.length) payload.interests = interestsList;
      const referenceRolesList = splitCSV(values.bezugspersonen);
      if (referenceRolesList.length) payload.referenceRoles = referenceRolesList;
      const placesList = splitCSV(values.orte);
      if (placesList.length) payload.places = placesList;

      await apiFetch<Child>('/api/children', {
        method: 'POST',
        body: payload,
      });

      navigate('/onboarding/push', { replace: true });
    } catch (err) {
      setIsSubmitting(false);
      if (err instanceof NetworkError) {
        setFormError('network');
      } else if (err instanceof ApiError && err.httpStatus === 403) {
        setFormError('emailNotVerified');
      } else {
        setFormError('childCreate');
      }
    }
  };

  const formErrorMessage =
    formError === 'network'
      ? S.errors.networkError
      : formError === 'parentUpdate'
        ? S.errors.parentUpdateFailed
        : formError === 'childCreate'
          ? S.errors.childCreateFailed
          : formError === 'emailNotVerified'
            ? S.errors.emailNotVerified
            : null;

  return (
    <>
      <button
        type="button"
        onClick={handleBack}
        className="mb-4 text-sm text-dream-white/80 hover:text-dream-white transition-colors"
      >
        {S.backLink}
      </button>

      <h1 className="text-[22px] font-semibold text-dream-white mb-4">{S.title}</h1>

      <div className="mb-6 flex gap-3 items-start p-3 rounded-lg bg-white/5 border border-white/10">
        <img
          src={ASSETS.kaptnKuschelImage}
          alt={STRINGS.onboarding.welcome.kaptnKuschelAlt}
          className="w-16 h-auto shrink-0"
          loading="eager"
        />
        <div className="text-xs text-dream-white/90 leading-relaxed">
          <p>{S.kaptnContextBody}</p>
          <a
            href={LEGAL_URLS.privacy}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 underline hover:text-dream-white"
          >
            {S.privacyLinkLabel}
          </a>
        </div>
      </div>

      {formErrorMessage && (
        <div
          role="alert"
          className="flex items-start gap-2 mb-4 p-3 rounded-md bg-[rgba(226,75,74,0.15)] border border-[rgba(226,75,74,0.4)] text-[#FCA5A5] text-sm"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{formErrorMessage}</p>
            {formError === 'emailNotVerified' &&
              (resendSuccess ? (
                <p className="mt-2 text-dream-white/85">
                  {STRINGS.builder.errorBanners.emailUnverified.resendSuccessBody}
                </p>
              ) : (
                <>
                  <Button
                    type="button"
                    onClick={handleResend}
                    disabled={resendLoading || rateLimited}
                    className="mt-2 h-9 px-3 text-sm bg-dream-navy text-dream-white hover:bg-dream-navy/90 disabled:opacity-60"
                  >
                    {resendLoading ? (
                      <>
                        <Loader2 aria-hidden className="mr-2 h-3.5 w-3.5 animate-spin" />
                        {STRINGS.builder.errorBanners.emailUnverified.resendLoadingLabel}
                      </>
                    ) : (
                      STRINGS.builder.errorBanners.emailUnverified.cta
                    )}
                  </Button>
                  {rateLimited && (
                    <p className="mt-1 text-xs text-dream-white/70">
                      {STRINGS.builder.errorBanners.emailUnverified.rateLimitedHint}
                    </p>
                  )}
                </>
              ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Parent first name (Briefing field 1 — onboarding-shell-level) */}
        <div className="space-y-1.5">
          <Label htmlFor="cp-parent-first" className="text-sm text-dream-white">
            {S.parentFirstNameLabel} *
          </Label>
          <Input
            id="cp-parent-first"
            type="text"
            autoComplete="given-name"
            value={parentFirstName}
            maxLength={100}
            onChange={(e) => {
              setParentFirstName(e.target.value);
              markDirty();
              if (errParentFirstName) setErrParentFirstName(null);
            }}
            onBlur={() => setErrParentFirstName(validateRequired(parentFirstName))}
            aria-invalid={Boolean(errParentFirstName)}
            className="h-11 bg-dream-white text-dream-navy"
          />
          {errParentFirstName && (
            <p className="text-xs text-[color:var(--color-error)]">{errParentFirstName}</p>
          )}
        </div>

        {/* Parent last name (Briefing field 2 — onboarding-shell-level) */}
        <div className="space-y-1.5">
          <Label htmlFor="cp-parent-last" className="text-sm text-dream-white">
            {S.parentLastNameLabel} *
          </Label>
          <Input
            id="cp-parent-last"
            type="text"
            autoComplete="family-name"
            value={parentLastName}
            maxLength={100}
            onChange={(e) => {
              setParentLastName(e.target.value);
              markDirty();
              if (errParentLastName) setErrParentLastName(null);
            }}
            onBlur={() => setErrParentLastName(validateRequired(parentLastName))}
            aria-invalid={Boolean(errParentLastName)}
            className="h-11 bg-dream-white text-dream-navy"
          />
          {errParentLastName && (
            <p className="text-xs text-[color:var(--color-error)]">{errParentLastName}</p>
          )}
        </div>

        {/* Child fields 3-12 — shared sub-component */}
        <ChildProfileFields
          values={values}
          onChange={handleChildFieldChange}
          errors={errors}
          onBlurField={handleChildFieldBlur}
          tone="dark"
        />

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSubmitting ? S.submitLoading : S.submitButton}
        </Button>
      </form>
    </>
  );
}
